// =============================================================================
//  VistaDelEjercicio.jsx
//  EL EJERCICIO ENTERO, PARA MIRARLO — NO PARA TOCARLO
//
//  Rob, 05-10-2026: «un botón que sea Ver, para que si lo activo pueda ver todo
//  el ejercicio que creó la IA, para validarlo y ver si cumple con las
//  exigencias, o si no editarlo por si ocurrió un error».
//
//  ── POR QUÉ NO VALE NINGUNA DE LAS DOS VISTAS QUE YA HABÍA ──────────────────
//  · La línea de resumen del paso 3 enseña 80 caracteres de la pregunta. Con eso
//    no se puede decidir nada: la trampa de un ejercicio malo está en los
//    distractores y en la explicación, que es justo lo que no se ve.
//  · El editor manual lo enseña todo, pero en campos editables. Para revisar
//    veinte ejercicios seguidos eso es peor, no mejor: cada clic por el camino
//    puede cambiar algo sin querer, y hay que acordarse de salir sin guardar.
//  · El Preview del paso 4 lo enseña JUGÁNDOLO, y eso esconde justo lo que se
//    quiere revisar — la respuesta correcta y el porqué sólo aparecen después
//    de contestar.
//
//  Así que esto es una tercera cosa a propósito: todo a la vista, con la
//  respuesta correcta marcada, y sin un solo sitio donde escribir.
// =============================================================================
import React from 'react';

const LETRAS = 'ABCDEFGHIJ';

const vacio = (v) => !(typeof v === 'string' ? v.trim() : v);

//  Un dato que falta se enseña, no se esconde: la vista existe para encontrar
//  justo eso. En gris y entre paréntesis, para que no se confunda con contenido.
const Falta = ({ children }) => <span className="ee-ver-falta">({children})</span>;

const Campo = ({ titulo, children }) => (
    <div className="ee-ver-campo">
        <div className="ee-ver-etiqueta">{titulo}</div>
        <div className="ee-ver-valor">{children}</div>
    </div>
);

export default function VistaDelEjercicio({ data, tipoPedido, etiquetas = {} }) {
    if (!data || typeof data !== 'object') {
        return <div className="ee-ver"><Falta>este ejercicio todavía no se ha generado</Falta></div>;
    }

    const tipo = data.type;
    //  Se pide un tipo y la IA devuelve otro. Los dos ejercicios están bien
    //  escritos, así que mirando éste solo no se nota — lo único que chirría es
    //  la etiqueta de arriba, y nadie la mira dos veces. El alumno acabaría
    //  recibiendo algo distinto de lo que se planeó.
    const otroTipo = tipoPedido && tipo && tipo !== tipoPedido;
    const explicacion = vacio(data.explanation)
        ? <Falta>sin explicación — el alumno no sabrá por qué falló</Falta>
        : data.explanation;

    return (
        <div className="ee-ver">
            {otroTipo && (
                <div className="ee-ver-alerta">
                    Se pidió «{etiquetas[tipoPedido] || tipoPedido}» y lo que se generó es
                    de tipo «{etiquetas[tipo] || tipo}». El alumno vería esto segundo.
                </div>
            )}
            {tipo === 'quiz' && <VistaQuiz data={data} />}
            {tipo === 'truefalse' && <VistaVerdaderoFalso data={data} />}
            {(tipo === 'fill' || tipo === 'dragdrop') && <VistaHuecos data={data} />}
            {tipo === 'wordselect' && <VistaVocabulario data={data} />}
            {tipo === 'match' && <VistaParejas data={data} />}
            {!['quiz', 'truefalse', 'fill', 'dragdrop', 'wordselect', 'match'].includes(tipo) && (
                <Campo titulo="Tipo no reconocido">
                    <pre className="ee-ver-crudo">{JSON.stringify(data, null, 2)}</pre>
                </Campo>
            )}
            <Campo titulo="Explicación (lo que lee el alumno)">{explicacion}</Campo>
        </div>
    );
}

function VistaQuiz({ data }) {
    const opciones = Array.isArray(data.choices) ? data.choices : [];
    const correcta = Number(data.correct);
    //  El índice lo escribe la IA y puede apuntar fuera de la lista. Si eso
    //  pasa no se marca ninguna y se avisa: marcar la primera «por si acaso»
    //  daría por bueno un ejercicio que nadie podría acertar.
    const fueraDeSitio = !Number.isInteger(correcta) || correcta < 0 || correcta >= opciones.length;
    return (
        <>
            <Campo titulo="Pregunta">
                {vacio(data.question) ? <Falta>sin pregunta</Falta> : data.question}
            </Campo>
            <Campo titulo="Opciones">
                {!opciones.length
                    ? <Falta>sin opciones</Falta>
                    : (
                        <ul className="ee-ver-lista">
                            {opciones.map((op, i) => (
                                <li key={i} className={`ee-ver-opcion ${correcta === i ? 'buena' : ''}`}>
                                    <span className="ee-ver-letra">{LETRAS[i] || i + 1}</span>
                                    <span>{op}</span>
                                    {correcta === i && <span className="ee-ver-marca">correcta</span>}
                                </li>
                            ))}
                        </ul>
                    )}
                {fueraDeSitio && opciones.length > 0 && (
                    <div className="ee-ver-alerta">
                        La respuesta correcta apunta a la opción {String(data.correct)}, que no existe.
                    </div>
                )}
            </Campo>
        </>
    );
}

function VistaVerdaderoFalso({ data }) {
    return (
        <>
            <Campo titulo="Afirmación">
                {vacio(data.statement) ? <Falta>sin afirmación</Falta> : data.statement}
            </Campo>
            <Campo titulo="Respuesta correcta">
                {data.answer === true && <span className="ee-ver-chip buena">VERDADERO</span>}
                {data.answer === false && <span className="ee-ver-chip buena">FALSO</span>}
                {data.answer !== true && data.answer !== false && <Falta>sin respuesta</Falta>}
            </Campo>
        </>
    );
}

function VistaHuecos({ data }) {
    const esArrastrar = data.type === 'dragdrop';
    const huecos = esArrastrar
        ? (Array.isArray(data.correctWords) ? data.correctWords : [])
        : (Array.isArray(data.blanks) ? data.blanks : []);
    const plantilla = typeof data.template === 'string' ? data.template : '';
    const enPlantilla = (plantilla.match(/____/g) || []).length;

    return (
        <>
            <Campo titulo="Plantilla">
                {vacio(plantilla) ? <Falta>sin plantilla</Falta>
                    //  Los huecos se pintan como huecos: leer «____» en medio de
                    //  una frase larga no deja contarlos de un vistazo.
                    : plantilla.split('____').map((trozo, i, todos) => (
                        <React.Fragment key={i}>
                            {trozo}
                            {i < todos.length - 1 && <span className="ee-ver-hueco">{i + 1}</span>}
                        </React.Fragment>
                    ))}
            </Campo>
            <Campo titulo={esArrastrar ? 'Palabras que se arrastran' : 'Huecos'}>
                {!huecos.length ? <Falta>ninguno</Falta> : (
                    <ul className="ee-ver-lista">
                        {huecos.map((correcta, i) => {
                            //  En «Completar», `choices[i]` trae la correcta
                            //  MEZCLADA con los distractores. Se separan para
                            //  que se vea quién es quién — mezclados es justo
                            //  como se cuela un distractor igual de válido.
                            const todas = Array.isArray(data.choices?.[i]) ? data.choices[i] : [];
                            const otras = todas.filter((c) => c !== correcta);
                            return (
                                <li key={i} className="ee-ver-opcion">
                                    <span className="ee-ver-letra">{i + 1}</span>
                                    <span className="ee-ver-chip buena">{correcta}</span>
                                    {!esArrastrar && (
                                        <span className="ee-ver-distractores">
                                            {otras.length ? otras.join(' · ') : <Falta>sin distractores</Falta>}
                                        </span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </Campo>
            {esArrastrar && (
                <Campo titulo="Distractores">
                    {Array.isArray(data.distractors) && data.distractors.length
                        ? data.distractors.join(' · ')
                        : <Falta>sin distractores</Falta>}
                </Campo>
            )}
            {enPlantilla !== huecos.length && (
                <div className="ee-ver-alerta">
                    La plantilla tiene {enPlantilla} huecos y hay {huecos.length} respuestas.
                </div>
            )}
        </>
    );
}

function VistaVocabulario({ data }) {
    const palabras = Array.isArray(data.targetWords) ? data.targetWords : [];
    return (
        <Campo titulo="Palabras que hay que encontrar">
            {!palabras.length ? <Falta>ninguna</Falta> : (
                <div className="ee-ver-chips">
                    {palabras.map((p, i) => <span key={i} className="ee-ver-chip buena">{p}</span>)}
                </div>
            )}
        </Campo>
    );
}

function VistaParejas({ data }) {
    const parejas = Array.isArray(data.pairs) ? data.pairs : [];
    return (
        <Campo titulo="Parejas">
            {!parejas.length ? <Falta>ninguna</Falta> : (
                <ul className="ee-ver-lista">
                    {parejas.map((p, i) => (
                        <li key={i} className="ee-ver-pareja">
                            <span className="ee-ver-chip buena">{vacio(p?.left) ? '—' : p.left}</span>
                            <span className="ee-ver-igual">=</span>
                            <span>{vacio(p?.right) ? <Falta>vacío</Falta> : p.right}</span>
                        </li>
                    ))}
                </ul>
            )}
        </Campo>
    );
}
